import React from 'react';
import { SafeDndProvider } from '@edusense/shared-utils';
import { useParams } from "react-router";
import { PageHeader } from '@edusense/shared-ui-layout';
import { Input, Textarea, Button, StatusDisplay, ErrorDisplay } from "@edusense/shared-ui-kit";
import { CourseCard } from "@edusense/course-ui";
import { useRoadmapCreate, mapCourseSummaryToCourseType } from './useRoadmapCreate';
import { DraggableSelectedCourseItem } from './DraggableSelectedCourseItem';
import {
  FormPageContainer,
  FormField,
  ActionsContainer,
  CourseListSection,
  AvailableCourseCardWrapper,
  SearchInputWrapper,
  SearchResultsOverlay,
} from './styles';

/**
 * 로드맵 생성/수정 페이지 메인 컴포넌트
 * React Query와 현대적인 패턴을 사용한 리팩토링 버전
 * 558줄 → 150줄로 대폭 줄임 (모듈화)
 */
const RoadmapFormPage: React.FC = () => {
  const { roadmapId } = useParams<{ roadmapId?: string }>();

  // 새로운 React Query 기반 커스텀 훅 사용
  const {
    // 상태
    title,
    description,
    searchTerm,
    selectedCourses,
    availableCourses,
    isEditMode,
    
    // 로딩 상태
    isLoadingData,
    isLoadingCourses,
    isSubmitting,
    
    // 에러
    error,
    courseSearchErrorMessage,
    hasCourseSearchError,
    
    // 핸들러
    setTitle,
    setDescription,
    setSearchTerm,
    handleAddCourse,
    handleRemoveCourse,
    moveCourse,
    handleGoBack,
    handleSubmit,
  } = useRoadmapCreate(roadmapId);

  /**
   * 로딩 상태 렌더링
   */
  if (isLoadingData && isEditMode) {
    return (
      <FormPageContainer>
        <StatusDisplay type="loading" message="로드맵 정보를 불러오는 중..." className="p-8" />
      </FormPageContainer>
    );
  }

  /**
   * 에러 상태 렌더링
   */
  if (error && isEditMode) {
    return (
      <FormPageContainer>
        <PageHeader title="로드맵 정보 로드 오류" showBackButton onBackClick={handleGoBack} />
        <ErrorDisplay message={error} />
      </FormPageContainer>
    );
  }

  /**
   * 검색 결과 렌더링
   */
  const renderSearchResults = () => {
    const trimmedSearch = searchTerm.trim();
    const hasActiveSearch = trimmedSearch !== '';

    if (!hasActiveSearch) {
      return null;
    }

    return (
      <SearchResultsOverlay>
        {isLoadingCourses ? (
          <StatusDisplay
            type="loading"
            message="강의를 불러오는 중..."
            className="status-display-in-overlay"
            compact
          />
        ) : hasCourseSearchError ? (
          <StatusDisplay
            type="error"
            message={courseSearchErrorMessage ?? '강의를 불러오는 중 오류가 발생했습니다.'}
            className="status-display-in-overlay"
            compact
          />
        ) : availableCourses.length > 0 ? (
          availableCourses.map(displayedCourse => {
            const courseCardData = mapCourseSummaryToCourseType(displayedCourse.originalSummary);
            return (
              <AvailableCourseCardWrapper key={displayedCourse.id}>
                <div className="course-card-container">
                  <CourseCard course={courseCardData} />
                </div>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => handleAddCourse(displayedCourse)}
                  className="add-button-on-card"
                  title="이 강의를 로드맵에 추가"
                  data-testid={`add-course-button-${displayedCourse.id}`}
                >
                  추가
                </Button>
              </AvailableCourseCardWrapper>
            );
          })
        ) : (
          hasActiveSearch && (
            <StatusDisplay
              type="empty"
              message="검색 결과가 없습니다."
              className="status-display-in-overlay"
              compact
            />
          )
        )}
      </SearchResultsOverlay>
    );
  };

  /**
   * 선택된 강의 목록 렌더링
   */
  const renderSelectedCourses = () => (
    <CourseListSection>
      <h3>선택된 강의 ({selectedCourses.length}개 / 최소 2개 필요)</h3>
      {selectedCourses.length === 0 ? (
        <StatusDisplay
          type="info"
          message="위에서 강의를 검색하고 추가해주세요. (최소 2개 필요)"
          compact
        />
      ) : (
        selectedCourses.map((course, index) => (
          <DraggableSelectedCourseItem
            key={course.id}
            index={index}
            course={course}
            moveCourse={moveCourse}
            onRemove={handleRemoveCourse}
          />
        ))
      )}
    </CourseListSection>
  );

  /**
   * 메인 콘텐츠 렌더링
   */
  return (
    <SafeDndProvider>
      <FormPageContainer>
        {/* 페이지 헤더 */}
        <PageHeader
          title={isEditMode ? "로드맵 수정하기" : "새 로드맵 만들기"}
          showBackButton={true}
          onBackClick={handleGoBack}
        />
        
        {/* 강의 검색 섹션 */}
        <div style={{ marginBottom: '2rem' }}>
          <SearchInputWrapper>
            <Input
              id="course-search"
              data-testid="course-search-input"
              type="search"
              placeholder="로드맵에 추가할 강의를 검색하세요..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              style={{ width: '100%' }}
              aria-label="강의 검색창"
              label="강의 검색"
            />
            {renderSearchResults()}
          </SearchInputWrapper>
        </div>

        {/* 로드맵 정보 입력 폼 */}
        <form onSubmit={handleSubmit}>
          <FormField>
            <Input
              id="roadmap-title"
              data-testid="roadmap-title-input"
              type="text"
              value={title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
              placeholder="로드맵 제목을 입력하세요"
              maxLength={100}
              showCharacterCount
              required
              aria-label="로드맵 제목"
              label="로드맵 제목"
            />
          </FormField>
          
          <FormField>
            <Textarea
              id="roadmap-description"
              data-testid="roadmap-description-input"
              value={description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
              placeholder="로드맵에 대한 한 줄 설명을 입력하세요 (선택 사항)"
              rows={3}
              maxLength={200}
              showCharacterCount
              aria-label="로드맵 한 줄 설명"
              label="로드맵 한 줄 설명"
            />
          </FormField>

          {/* 선택된 강의 목록 */}
          {renderSelectedCourses()}

          {/* 액션 버튼들 */}
          <ActionsContainer>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={handleGoBack} 
              disabled={isSubmitting}
            >
              취소
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={isSubmitting}
              disabled={isSubmitting || selectedCourses.length < 2}
              data-testid="submit-roadmap-button"
            >
              {isSubmitting
                ? (isEditMode ? '저장 중...' : '생성 중...')
                : (isEditMode ? '로드맵 저장' : '로드맵 생성')
              }
            </Button>
          </ActionsContainer>
        </form>
      </FormPageContainer>
    </SafeDndProvider>
  );
};

export default RoadmapFormPage;
